"use client"

import { useState, useEffect } from 'react'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { useSession } from '@/features/auth/context/session.context'
import { authFetchJson } from '@/shared/lib/auth-fetch'

export interface WorkScope {
    id: number
    workScope: string
    workScopeDisplay: string | null
    toolsNeeded: string[]
    suppliesNeeded: string[]
    regionWorkScopeId?: number | null
}

export interface WorkScopeOption {
    value: string
    label: string
}

/** Region from URL, falling back to the user's profile selectedRegion on top-level routes. */
export function useEffectiveRegionId() {
    const { regionId: urlRegionId, region, regionSlug, isLoading } = useRegionId()
    const { profile } = useSession()
    const regionId = urlRegionId ?? profile?.selectedRegion ?? null

    return { regionId, region, regionSlug, isLoading }
}

export function useWorkScopes(regionIdOverride?: number | null) {
    const { regionId: effectiveRegionId, isLoading: isRegionLoading } = useEffectiveRegionId()
    const regionId = regionIdOverride !== undefined ? regionIdOverride : effectiveRegionId
    const [workScopes, setWorkScopes] = useState<WorkScope[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        async function fetchWorkScopes() {
            if (isRegionLoading && regionIdOverride === undefined) {
                return
            }

            try {
                setLoading(true)
                setError(null)
                const url = regionId
                    ? `/work-scopes/priority-table?regionId=${regionId}`
                    : '/work-scopes/priority-table'

                const res = await authFetchJson<WorkScope[]>(url)

                if (!res.success) {
                    throw new Error(res.message || 'Failed to fetch work scopes')
                }

                const data = res.data
                setWorkScopes(Array.isArray(data) ? data : [])
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred')
                console.error('Error fetching work scopes:', err)
            } finally {
                setLoading(false)
            }
        }

        fetchWorkScopes()
    }, [regionId, isRegionLoading, regionIdOverride])

    const workScopeOptions: WorkScopeOption[] = workScopes.map(scope => ({
        value: scope.workScope,
        label: scope.workScopeDisplay || scope.workScope
    }))

    return { workScopes, workScopeOptions, loading, error, regionId }
}

export function useWorkScope(
    workScopeName: string | null,
    regionIdOverride?: number | null,
) {
    const { regionId: effectiveRegionId, isLoading: isRegionLoading } = useEffectiveRegionId()
    const regionId = regionIdOverride !== undefined ? regionIdOverride : effectiveRegionId
    const [workScope, setWorkScope] = useState<WorkScope | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!workScopeName) {
            setWorkScope(null)
            setLoading(false)
            setError(null)
            return
        }

        if (isRegionLoading && regionIdOverride === undefined) {
            return
        }

        async function fetchWorkScope(name: string) {
            try {
                setLoading(true)
                setError(null)
                const workScopeToFetch =
                    name.toLowerCase() === 'alternative-ministry' ||
                    name.toLowerCase() === 'alt-ministry'
                        ? 'misc'
                        : name

                const url = regionId
                    ? `/work-scopes/by-name?workScope=${encodeURIComponent(workScopeToFetch)}&regionId=${regionId}`
                    : `/work-scopes/by-name?workScope=${encodeURIComponent(workScopeToFetch)}`

                const res = await authFetchJson<WorkScope>(url)

                if (!res.success) {
                    throw new Error(res.message || 'Failed to fetch work scope')
                }

                const data = res.data
                setWorkScope(data)
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred')
                console.error('Error fetching work scope:', err)
                setWorkScope(null)
            } finally {
                setLoading(false)
            }
        }

        fetchWorkScope(workScopeName)
    }, [workScopeName, regionId, isRegionLoading, regionIdOverride])

    return { workScope, loading, error, regionId }
}

